// Plain text rules shared by every client and the API. Kept free of dependencies so that
// clients can use them cheaply.

/** Longest text accepted for rewriting, in Unicode code points. Bounds the cost of each request. */
export const MAX_TEXT_LENGTH = 5_000;

const SURROGATE_PAIR = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;

/**
 * Counts Unicode code points, so an emoji counts as one character rather than two UTF-16 units.
 * The client and the API share this definition so their length limits always agree.
 */
export function countCharacters(text: string): number {
  return text.length - (text.match(SURROGATE_PAIR)?.length ?? 0);
}

// Scripts written without spaces between words, where a run of non-whitespace can be a whole
// paragraph.
const UNSPACED_SCRIPT =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}]/u;

let wordSegmenter: Intl.Segmenter | undefined;

/** Words in a run of unspaced script, found with the dictionaries built into Intl. */
function countSegmentedWords(run: string): number {
  // Browsers from before Intl.Segmenter count the run as one word, as before. The API always
  // has it, so metering stays right; only such a browser's word count comes out low.
  if (typeof Intl.Segmenter !== 'function') return 1;
  wordSegmenter ??= new Intl.Segmenter(undefined, { granularity: 'word' });
  let words = 0;
  for (const segment of wordSegmenter.segment(run)) if (segment.isWordLike) words++;
  return Math.max(1, words);
}

/**
 * Characters, not counting whitespace, that count as at least one word. Well above the average
 * word in any language Lyrmo writes, even Finnish at about 7, so ordinary text is counted by its
 * spaces alone.
 */
const CHARACTERS_PER_WORD = 10;

function wordsAndCharacters(text: string): { words: number; characters: number } {
  let words = 0;
  let characters = 0;
  for (const run of text.split(/\s+/u)) {
    if (!run) continue;
    words += UNSPACED_SCRIPT.test(run) ? countSegmentedWords(run) : 1;
    characters += countCharacters(run);
  }
  return { words, characters };
}

/**
 * Counts words as they're written: runs of non-whitespace, except in Chinese, Japanese, Thai, and
 * other scripts written without spaces, where each word counts. Not for metering: text that hides
 * its spaces passes for a few words here.
 */
export const countWrittenWords = (text: string): number => wordsAndCharacters(text).words;

/**
 * Counts words as they're written, but at least one for every CHARACTERS_PER_WORD characters, so
 * text that joins its words with punctuation or invisible characters, or runs them together,
 * still counts for its length. The client and the API meter usage with this.
 */
export function countWords(text: string): number {
  const { words, characters } = wordsAndCharacters(text);
  return Math.max(words, Math.floor(characters / CHARACTERS_PER_WORD));
}
